import React, { useState, useCallback, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { toast } from 'sonner';
import { Heart } from 'lucide-react';
import { api } from '@/lib/api/client';
import { useAuthStore } from '@/lib/stores/auth';
import { cn } from '@/lib/utils';

interface LikeButtonProps {
  userId: string | number;
  streamId: string;
  initialLikes?: number;
  initialIsLiked?: boolean;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'outline' | 'ghost';
  showCount?: boolean;
  className?: string;
}

interface LikeData {
  isLiked: boolean;
  likeCount: number;
}

export function LikeButton(props: LikeButtonProps) {
  const {
    streamId,
    initialLikes = 0,
    initialIsLiked,
    size = 'md',
    variant = 'ghost',
    showCount = true,
    className,
  } = props;
  const { user } = useAuthStore();
  const [likeData, setLikeData] = useState<LikeData>({
    isLiked: initialIsLiked ?? false,
    likeCount: initialLikes
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const hasTrustedInitialState =
    Object.prototype.hasOwnProperty.call(props, 'initialIsLiked') ||
    Object.prototype.hasOwnProperty.call(props, 'initialLikes');

  useEffect(() => {
    if (hasTrustedInitialState) {
      setLikeData({
        isLiked: initialIsLiked ?? false,
        likeCount: initialLikes,
      });
    }
  }, [initialIsLiked, initialLikes, hasTrustedInitialState]);

  // Fetch current like status only when the caller did not provide a trusted initial state.
  useEffect(() => {
    if (user && !hasTrustedInitialState) {
      fetchLikeStatus();
    }
  }, [user, streamId, hasTrustedInitialState]);

  const fetchLikeStatus = async () => {
    try {
      const response = await api.get(`/api/streams/${streamId}/likes/status`);
      if (response.success && response.data) {
        const data = response.data as { isLiked: boolean; likeCount: number };
        setLikeData({
          isLiked: data.isLiked,
          likeCount: data.likeCount
        });
      }
    } catch (error) {
      console.error('Failed to fetch like status:', error);
    }
  };

  const handleLikeToggle = useCallback(async () => {
    if (!user) {
      toast.error('Please log in to like streams');
      return;
    }

    if (isLoading) return;

    setIsLoading(true);

    // Optimistic update
    const previousState = { ...likeData };
    setLikeData(prev => ({
      isLiked: !prev.isLiked,
      likeCount: prev.isLiked ? prev.likeCount - 1 : prev.likeCount + 1
    }));

    // Animation
    setIsAnimating(true);
    setTimeout(() => setIsAnimating(false), 600);

    try {
      const endpoint = `/api/streams/${streamId}/likes`;
      const method = likeData.isLiked ? 'DELETE' : 'POST';

      const response = method === 'POST'
        ? await api.post(endpoint, {}, { headers: { 'Content-Type': 'application/json' } })
        : await api.delete(endpoint);

      if (response.success && response.data) {
        const data = response.data as { isLiked: boolean; likeCount: number; message?: string };
        // Update with server response
        setLikeData({
          isLiked: data.isLiked,
          likeCount: data.likeCount
        });

      } else {
        // Revert optimistic update on error
        setLikeData(previousState);
        toast.error('Failed to update like', {
          description: response.error?.message || 'Please try again'
        });
      }
    } catch (error: unknown) {
      // Revert optimistic update on error
      setLikeData(previousState);
      console.error('Like toggle error:', error);
      toast.error('Failed to update like', {
        description: 'Please check your connection and try again'
      });
    } finally {
      setIsLoading(false);
    }
  }, [user, streamId, likeData, isLoading]);

  const formatLikeCount = (count: number): string => {
    if (count >= 1000000) {
      return `${(count / 1000000).toFixed(1)}M`;
    }
    if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}K`;
    }
    return count.toString();
  };

  const sizeClasses = {
    sm: 'h-8 px-2 text-xs',
    md: 'h-9 px-3 text-sm',
    lg: 'h-10 px-4 text-base'
  };

  const iconSizes = {
    sm: 'h-3 w-3',
    md: 'h-4 w-4',
    lg: 'h-5 w-5'
  };

  return (
    <Button
      variant={variant}
      size="sm"
      onClick={handleLikeToggle}
      disabled={isLoading || !user}
      className={cn(
        'flex items-center gap-2 transition-all duration-200',
        likeData.isLiked && 'text-destructive hover:text-destructive',
        isAnimating && likeData.isLiked && 'animate-pulse scale-110',
        sizeClasses[size],
        className
      )}
      aria-label={
        likeData.isLiked
          ? `Unlike this stream (${likeData.likeCount} likes)`
          : `Like this stream (${likeData.likeCount} likes)`
      }
      aria-pressed={likeData.isLiked}
    >
      {isLoading ? (
        <LoadingSpinner size="sm" />
      ) : (
        <Heart
          className={cn(
            iconSizes[size],
            'transition-all duration-200',
            likeData.isLiked && 'fill-current',
            isAnimating && likeData.isLiked && 'scale-125'
          )}
        />
      )}

      {showCount && (
        <span className="font-medium tabular-nums">
          {formatLikeCount(likeData.likeCount)}
        </span>
      )}
    </Button>
  );
}
